import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from "react";

const STORAGE_KEY = "vb_impersonation";

interface StoredImpersonation {
  userId: string;
  email: string;
}

interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedUserId: string | null;
  impersonatedEmail: string | null;
  startImpersonation: (userId: string, email: string) => void;
  stopImpersonation: () => void;
}

const ImpersonationContext = createContext<ImpersonationState | undefined>(undefined);

function loadFromStorage(): StoredImpersonation | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredImpersonation;
    if (parsed.userId && parsed.email) return parsed;
    return null;
  } catch {
    return null;
  }
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const stored = loadFromStorage();
  const [impersonatedUserId, setImpersonatedUserId] = useState<string | null>(stored?.userId ?? null);
  const [impersonatedEmail, setImpersonatedEmail] = useState<string | null>(stored?.email ?? null);

  // Sync to sessionStorage whenever state changes
  useEffect(() => {
    if (impersonatedUserId && impersonatedEmail) {
      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ userId: impersonatedUserId, email: impersonatedEmail })
      );
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [impersonatedUserId, impersonatedEmail]);

  const startImpersonation = useCallback((userId: string, email: string) => {
    setImpersonatedUserId(userId);
    setImpersonatedEmail(email);
  }, []);

  const stopImpersonation = useCallback(() => {
    setImpersonatedUserId(null);
    setImpersonatedEmail(null);
  }, []);

  return (
    <ImpersonationContext.Provider value={{
      isImpersonating: !!impersonatedUserId,
      impersonatedUserId,
      impersonatedEmail,
      startImpersonation,
      stopImpersonation,
    }}>
      {children}
    </ImpersonationContext.Provider>
  );
}

export function useImpersonation() {
  const context = useContext(ImpersonationContext);
  if (!context) throw new Error("useImpersonation must be used within ImpersonationProvider");
  return context;
}
